import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getCompanySettings, updateCompanySettings } from "@/lib/api";
import { Save } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import {
  CompanyInfoCard, CocCard, WorkScheduleCard, BrandingCard, BackupCard, LotNumberingCard, YieldCalcCard, WeeklyDigestCard, AdminAlertsCard,
} from "./company-settings/sections";

export default function CompanySettings() {
  const { t } = useT();
  const { user } = useAuth();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => { setF(await getCompanySettings()); })(); }, []);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const toggleWorkingDay = (d) => {
    setF((x) => {
      const list = Array.isArray(x.working_days) ? x.working_days : [0, 1, 2, 3, 4];
      const next = list.includes(d) ? list.filter((v) => v !== d) : [...list, d];
      return { ...x, working_days: next.sort((a, b) => a - b) };
    });
  };

  const toggleDeliveryDay = (d) => {
    setF((x) => {
      const base = Array.isArray(x.delivery_days) ? x.delivery_days : (Array.isArray(x.working_days) ? x.working_days : [0, 1, 2, 3, 4]);
      const next = base.includes(d) ? base.filter((v) => v !== d) : [...base, d];
      return { ...x, delivery_days: next.sort((a, b) => a - b) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const emailsList = String(f.weekly_digest_emails_text ?? (Array.isArray(f.weekly_digest_emails) ? f.weekly_digest_emails.join(", ") : ""))
        .split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
      // Prune per-recipient overrides to emails still on the list (lowercased keys).
      const emailSet = new Set(emailsList.map((e) => e.toLowerCase()));
      const recipSections = Object.fromEntries(
        Object.entries(f.weekly_digest_recipient_sections || {}).filter(([e]) => emailSet.has(e))
      );
      await updateCompanySettings({
        legal_name: f.legal_name || "", address: f.address || "", tax_id: f.tax_id || "",
        reg_no: f.reg_no || "", phone: f.phone || "", email: f.email || "",
        coc_title: f.coc_title || "Certificate of Conformity",
        declaration_text: f.declaration_text || "", footer_text: f.footer_text || "",
        standard_workday_hours: Number(f.standard_workday_hours) || 8,
        working_days: Array.isArray(f.working_days) ? f.working_days : [0, 1, 2, 3, 4],
        delivery_days: Array.isArray(f.delivery_days) ? f.delivery_days : (Array.isArray(f.working_days) ? f.working_days : [0, 1, 2, 3, 4]),
        brand_name: f.brand_name || "", logo_url: f.logo_url || "", accent_color: f.accent_color || "",
        lot_include_year: !!f.lot_include_year,
        milk_density_kg_per_l: Number(f.milk_density_kg_per_l) > 0 ? Number(f.milk_density_kg_per_l) : 1.03,
        milk_max_temp_c: Number(f.milk_max_temp_c) > 0 ? Number(f.milk_max_temp_c) : 6,
        weekly_digest_enabled: f.weekly_digest_enabled !== false,
        weekly_digest_emails: emailsList,
        // Section picks are admin-only — the backend rejects them from managers.
        ...(user?.role === "admin" && f.weekly_digest_sections ? { weekly_digest_sections: f.weekly_digest_sections } : {}),
        ...(user?.role === "admin" ? { weekly_digest_recipient_sections: recipSections } : {}),
      });
      toast.success(t("settings.saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("settings.failedSave")); }
    finally { setSaving(false); }
  };

  if (!f) return <Layout><div className="text-sm text-muted-foreground">{t("common.loading")}</div></Layout>;

  return (
    <Layout actions={
      <Button size="sm" onClick={save} disabled={saving} data-testid="company-save-button">
        <Save className="mr-1.5 h-4 w-4" /> {t("settings.saveBtn")}
      </Button>
    }>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <CompanyInfoCard f={f} set={set} />
        <CocCard f={f} set={set} />
        <BrandingCard f={f} set={set} />
        <LotNumberingCard f={f} set={set} />
        <YieldCalcCard f={f} set={set} />
        <WeeklyDigestCard f={f} set={set} />
        <AdminAlertsCard />
        <WorkScheduleCard f={f} set={set} toggleWorkingDay={toggleWorkingDay} toggleDeliveryDay={toggleDeliveryDay} />
        <BackupCard />
      </div>
    </Layout>
  );
}
